<template>
    <div id="pxf-content">
        <h4>
            <span class="visually-hidden" v-html="toFoisLabel(headerBlockDto.title)" />
            <span aria-hidden="true">{{ headerBlockDto.title }}</span>
        </h4>
        <p>
            <strong>{{ headerBlockDto.subTitle }}</strong>
        </p>

        <p class="lead mb-0">
            <strong>{{ tableTitle }}</strong>
        </p>
        <div id="table-description" class="lead" v-html="tableSubTitle" />
        <div class="table-container">
            <table class="table text-gray3" aria-labelledby="table-description">
                <caption>Détail de financement</caption>
                <thead class="text-dark lead">
                    <tr>
                        <th class="px-1 pb-1" scope="col">
                            Les modalités
                        </th>
                        <th
                            v-for="duration in durations"
                            :key="duration"
                            class="px-1 pb-1"
                            scope="col"
                        >
                            <span class="visually-hidden">{{ duration + " mensualités" }}</span>
                            <span aria-hidden="true">{{ duration }}x</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            Mensualité
                        </th>
                        <td v-for="(amount, i) in monthlyAmounts" :key="'amount'+i" class="px-1 pb-1">
                            {{ amount }}
                        </td>
                    </tr>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            Montant total dû
                        </th>
                        <td v-for="(total, i) in totalAmounts" :key="'total'+i" class="px-1 pb-1">
                            {{ total }}
                        </td>
                    </tr>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            TAEG fixe
                        </th>
                        <td v-for="(taeg, i) in taegRates" :key="'taeg'+i" class="px-1 pb-1">
                            {{ taeg }}
                        </td>
                    </tr>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            Taux débiteur fixe
                        </th>
                        <td v-for="(nominal, i) in nominalRates" :key="'nominal'+i" class="px-1 pb-1">
                            {{ nominal }}
                        </td>
                    </tr>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            Coût du crédit
                        </th>
                        <td v-for="(cost, i) in creditCosts" :key="'cost'+i" class="px-1 pb-1">
                            {{ cost }}
                        </td>
                    </tr>
                    <tr>
                        <th class="px-1 pb-1" scope="row">
                            Montant du financement
                        </th>
                        <td v-for="(funding, i) in fundingAmounts" :key="'funding-'+i" class="px-1 pb-1">
                            {{ funding }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <p class="small">
            <strong>{{ pxfLegalMentionsText }}</strong>
        </p>

        <!--Solutions de financement-->
        <ob1-liens-haut-bas
            :is-collapsed="false"
            :is-large="true"
            collapsed-block-id="financing-pxf-block"
            :button-label="financingBlock.title"
            :aria-button-label="toFoisLabel(financingBlock.title)"
            button-id="financing-pxf-link"
        >
            <p v-html="financingBlock.text" />
        </ob1-liens-haut-bas>

        <!--Illustration génie-->
        <div class="row pt-2 pb-1">
            <ob1-picture :visual="illustration" class="illustration" css-classes="d-block mx-auto" />
        </div>

        <!--Bloc comment profiter-->
        <div class="bg-gray2 px-2 pb-1 mb-1 mb-md-2">
            <p class="mb-0">
                <strong>{{ howToBenefitDto.title }}</strong>
                <br>
                <strong>{{ howToBenefitDto.subTitle }}</strong>
            </p>
            <p
                v-for="(howToBenefitText, index) in howToBenefitDto.texts"
                :key="'how-to-'+index"
                class="d-flex align-items-start mb-0"
            >
                <span class="font-size-24" :class="'icon-number-' + getIndex(index) +'-wired'" aria-hidden="true" />
                <span class="ms-1 how-to-benefit" v-html="howToBenefitText" />
            </p>
        </div>

        <!--Bouton en savoir plus-->
        <div class="text-center mt-2 mb-4">
            <ob1-boutons
                :label="howToBenefitDto.link.label"
                :href="howToBenefitDto.link.url"
                :target="'_blank'"
                :is-button="false"
                is-secondary
            />
        </div>

        <!--Mentions legales-->
        <ob1-liens-haut-bas
            :is-collapsed="false"
            collapsed-block-id="legal-mentions-pxf-block"
            :button-label="cgu.title"
            button-id="legal-mentions-pxf-link"
        >
            <p class="small" v-html="cgu.text" />
        </ob1-liens-haut-bas>
    </div>
</template>
<script>
export default {
    name: 'PxfModalContent',
    props: {
        headerBlockDto: {
            type: Object,
            required: true
        },
        examplePxfData: {
            type: Array,
            required: true
        },
        versionPxfData: {
            default: undefined,
            type: Array,
            required: false
        },
        pxfLegalMentionsText: {
            type: String,
            required: true
        },
        illustration: {
            type: Object,
            required: true
        },
        cgu: {
            type: Object,
            required: true
        },
        financingBlock: {
            type: Object,
            required: true
        },
        howToBenefitDto: {
            type: Object,
            required: true
        },
        examplePxfTabTitle: {
            type: String,
            required: true
        },
        examplePxfTabSubtitle: {
            type: String,
            required: true
        },
        versionPxfTabTitle: {
            type: String,
            required: true
        }
    },
    computed: {
        tableTitle() {
            return this.versionPxfData && this.versionPxfData.length ? this.versionPxfTabTitle : this.examplePxfTabTitle
        },
        tableSubTitle() {
            return this.versionPxfData && this.versionPxfData.length ? "" : this.examplePxfTabSubtitle
        },
        creditData() {
            return this.versionPxfData && this.versionPxfData.length ? this.versionPxfData : this.examplePxfData
        },
        durations() {
            return this.creditData.map(data => data.duration);
        },
        monthlyAmounts() {
            return this.creditData.map(data => this.formatPrice(data.monthlyAmountInCents));
        },
        totalAmounts() {
            return this.creditData.map(data => this.formatPrice(data.totalAmountWithFeesInCents));
        },
        taegRates() {
            return this.creditData.map(data => this.formatRate(data.taegRate));
        },
        nominalRates() {
            return this.creditData.map(data => this.formatRate(data.nominalRate));
        },
        creditCosts() {
            return this.creditData.map(data => this.formatPrice(data.interestsInCents));
        },
        fundingAmounts() {
            return this.creditData.map(data => this.formatPrice(data.fundingAmount));
        }
    },
    methods: {
        getIndex: function (index) {
            return index + 1;
        },
        formatPrice(amountInCents) {
            let amount = (amountInCents / 100).toFixed(2).toString();
            if (amount.endsWith('.00')) {
                return amount.slice(0, -3) + ' €';
            } else {
                return amount.replace('.', ',') + ' €';
            }
        },
        formatRate(rate) {
            return rate.toFixed(2).toString().replace('.', ',') + '%';
        },
        toFoisLabel(html) {
            return (html || '').replace(/(\d+)\s*x\b/gi, '$1 fois');
        }
    }
}
</script>

<style lang="scss" scoped>
@import 'ob1_colors';
@import 'ob1_common';

.table-container {
    overflow-x: auto;
}

td, th {
    min-width: px-to-rem(100);
}

tbody td:first-child, thead tr:first-child th:first-child {
    position: sticky;
    left: 0;
    background-color: $white;
    min-width: px-to-rem(109);
}

tbody td:nth-child(even), thead tr:first-child th:nth-child(even) {
    background-color: $gray2
}

tbody tr:nth-child(1), tr:nth-child(2) {
    font-weight: bold;
    color: $black;
}

.table-container .table thead tr, .table-container .table tbody tr {
    border-color: $gray3;
}
.illustration {
    height: px-to-rem(178);
}

</style>

<style lang="scss">
@import 'ob1_common';

.how-to-benefit ol{
    padding: px-to-rem(15) px-to-rem(15) 0;
    margin: 0;
}

</style>
